// İlaç ve besin listelerinin ortak satırları + ilaç formu yardımcıları
// (Tedavi, Reçete ve Diyet editörleri). Kurallar: docs/design-principles.md §8.3

import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalPharmacy as LocalPharmacyIcon,
  Restaurant as RestaurantIcon,
} from "@mui/icons-material";

import { COLORS } from "../../../styles/uiConstants";
import { ITEM_ROW_SX, ITEM_NAME_SX } from "./_editorParts";

export const getRefId = (value) =>
  value && typeof value === "object" ? value._id : value;

export const EMPTY_MEDICINE_FORM = {
  medicine: null,
  dose1: "",
  dose2: "",
  usagePeriodValue: "",
  usagePeriodUnit: "Gün",
  box: "",
  quantity: "",
  usageMethod: "",
};

const str = (v) => (v != null ? String(v) : "");

/** Listeden seçilen ilaç → varsayılanlarıyla dolu form. */
export const medicineFormFromTemplate = (v) =>
  v
    ? {
        medicine: v,
        dose1: str(v.dose1),
        dose2: str(v.dose2),
        usagePeriodValue: str(v.usagePeriod?.value),
        usagePeriodUnit: v.usagePeriod?.unit || "Gün",
        box: str(v.box),
        quantity: str(v.quantity),
        usageMethod: str(v.usageMethod),
      }
    : EMPTY_MEDICINE_FORM;

/** Kayıttaki ilaç → düzenleme formu. */
export const medicineFormFromEntry = (m) => ({
  medicine: { _id: getRefId(m.medicine), name: m.name || m.medicine?.name || "İlaç" },
  dose1: str(m.dose1),
  dose2: str(m.dose2),
  usagePeriodValue: str(m.usagePeriod?.value),
  usagePeriodUnit: m.usagePeriod?.unit || "Gün",
  box: str(m.box),
  quantity: str(m.quantity),
  usageMethod: m.usageMethod || "",
});

/** Kutu ve adet zorunlu. */
export const canSubmitMedicineForm = (form) =>
  Boolean(form.medicine && String(form.box ?? "").trim() && String(form.quantity ?? "").trim());

/** Form → kayıttaki ilaç biçimi (kutu/adet sayı değilse null). */
export const medicineEntryFromForm = (form, previous = {}) => {
  const box = Number(form.box);
  const quantity = Number(form.quantity);
  if (!form.medicine || !Number.isFinite(box) || !Number.isFinite(quantity)) return null;
  return {
    ...previous,
    medicine: form.medicine._id,
    name: form.medicine.name || previous.name || "",
    dose1: form.dose1 ? Number(form.dose1) : undefined,
    dose2: form.dose2 ? Number(form.dose2) : undefined,
    usagePeriod: form.usagePeriodValue
      ? { value: Number(form.usagePeriodValue) || 0, unit: form.usagePeriodUnit || "Gün" }
      : undefined,
    box,
    quantity,
    usageMethod: form.usageMethod || "",
    isTaken: Boolean(previous.isTaken),
  };
};

export const medicationSummary = (m) =>
  [
    m.dose1 != null && `Doz: ${m.dose1}${m.dose2 != null ? `×${m.dose2}` : ""}`,
    m.usagePeriod?.value && `${m.usagePeriod.value} ${m.usagePeriod.unit}`,
    m.box != null && `Kutu: ${m.box}`,
    m.quantity != null && `Adet: ${m.quantity}`,
    m.usageMethod,
  ]
    .filter(Boolean)
    .join(" · ");

const iconBtnSx = (hover) => ({ color: COLORS.slate400, "&:hover": { color: hover } });

/** İlaç satırları: ikon · ad + özet · (düzenle) · kaldır. */
export function MedicationRows({ items, palette, onEdit, onRemove }) {
  return (
    <Box>
      {items.map((m, idx) => {
        const name = m.name || (typeof m.medicine === "object" ? m.medicine?.name : null) || "İlaç";
        return (
          <Box key={`${getRefId(m.medicine) || idx}-${idx}`} sx={ITEM_ROW_SX}>
            <LocalPharmacyIcon sx={{ color: palette.primary, fontSize: 18, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={ITEM_NAME_SX} noWrap title={name}>
                {name}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: COLORS.slate500 }} noWrap>
                {medicationSummary(m)}
              </Typography>
            </Box>
            {onEdit ? (
              <IconButton size="small" aria-label={`${name} düzenle`} onClick={() => onEdit(idx)} sx={iconBtnSx(palette.primary)}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
            {onRemove ? (
              <IconButton size="small" aria-label={`${name} kaldır`} onClick={() => onRemove(idx)} sx={iconBtnSx(COLORS.errorDark)}>
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

/** Besin satırları: renkli ikon (serbest yeşil / yasaklı kırmızı) · ad · kaldır. */
export function FoodRows({ items, color, onRemove }) {
  return (
    <Box>
      {items.map((f, idx) => (
        <Box key={`${f._id || f.food}-${idx}`} sx={ITEM_ROW_SX}>
          <RestaurantIcon sx={{ color, fontSize: 18, flexShrink: 0 }} />
          <Typography sx={{ ...ITEM_NAME_SX, flex: 1 }} noWrap title={f.name}>
            {f.name || "—"}
          </Typography>
          {onRemove ? (
            <IconButton size="small" aria-label={`${f.name || "Besin"} kaldır`} onClick={() => onRemove(idx)} sx={iconBtnSx(COLORS.errorDark)}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

/** Listeden seçilen besin → kayıttaki besin biçimi. */
export const foodEntry = (food) => {
  const id = food?._id || food?.id;
  if (!id) return null;
  return {
    _id: id,
    food: id,
    name: food.name || "",
    amount: food.amount != null && food.amount !== "" ? String(food.amount) : "",
    amountUnit: food.amountUnit != null ? String(food.amountUnit) : "",
    notes: "",
  };
};

/** Mevcut listeye besinleri tekrarsız ekler. */
export const appendFoods = (current, foods) => {
  const seen = new Set((current || []).map((v) => String(v._id || v.food)));
  const next = [...(current || [])];
  foods.forEach((f) => {
    const entry = foodEntry(f);
    if (!entry || seen.has(String(entry._id))) return;
    seen.add(String(entry._id));
    next.push(entry);
  });
  return next;
};
